@if (application) {
  <!-- Status Badge -->
  <div class="status-section">
    <span class="label">Status:</span>
    <span class="status-badge status-{{ getStatusClass(application.status) }}">
      {{ getStatusLabel(application.status) }}
    </span>
    @if (application.status === ConnectionSelfSignupStatus.Approved) {
      <span class="label" style="margin-left: 16px">Joined:</span>
      <span
        class="status-badge status-{{ getJoinedClass(application.joined) }}"
      >
        {{ getJoinedLabel(application.joined) }}
      </span>
    }
  </div>

  <!-- Organization Details -->
  <div class="section">
    <h3 class="section-title">Basic Information</h3>
    <div class="details-grid">
      <div class="detail-item">
        <span class="label">Organization Name:</span>
        <span class="value">{{ application.organization_name }}</span>
      </div>
      @if (application.connection_display_name) {
        <div class="detail-item">
          <span class="label">Connection:</span>
          <span class="value">{{ application.connection_name }}</span>
        </div>
      }
      <div class="detail-item">
        <span class="label">Organization Type:</span>
        <span class="value">{{ getOrgTypeLabel(application.org_type) }}</span>
      </div>
      <div class="detail-item">
        <span class="label">Auth0 Configuration:</span>
        <span class="value">{{
          application.has_it_support
            ? "Self-configured (IT Support available)"
            : "System-managed (No IT Support)"
        }}</span>
      </div>
      @if (
        application.org_type === OrgType.Other && application.org_type_desc
      ) {
        <div class="detail-item">
          <span class="label">Organization Type Description:</span>
          <span class="value">{{ application.org_type_desc }}</span>
        </div>
      }
      <div class="detail-item">
        <span class="label">Contact Email:</span>
        <span class="value">{{ application.contact_email }}</span>
      </div>
      <div class="detail-item">
        <span class="label">Website:</span>
        <span class="value"
          ><a [href]="ensureHttpProtocol(application.website)" target="_blank">
            {{ application.website }}
          </a></span
        >
      </div>
      <div class="detail-item">
        <span class="label">Submitted on:</span>
        <span class="value">{{
          application.created_on_utc | date: "medium"
        }}</span>
      </div>
      @if (application.status !== ConnectionSelfSignupStatus.Rejected) {
        <div class="detail-item">
          <span class="label">Invitation link type:</span>
          <span class="value">{{
            application.connection_type === ConnectionType.Auth0
              ? "Auth0 self-service link"
              : "DH invitation link"
          }}</span>
        </div>
      }
    </div>
  </div>

  <!-- Service Region -->
  <div class="section">
    <h3 class="section-title">Service Region</h3>
    <div class="region-grid">
      @if (
        application.service_region.state &&
        application.service_region.state.length > 0
      ) {
        <div class="region-item">
          <div class="region-item-header">
            <span class="label">
              States
              <span class="item-count"
                >({{ application.service_region.state.length }})</span
              >
            </span>
            @if (application.service_region.state.length > REGION_ITEM_LIMIT) {
              <button
                type="button"
                class="btn-toggle-expand"
                (click)="toggleCategoryExpand('state')"
                [attr.aria-expanded]="isCategoryExpanded('state')"
              >
                <span>{{
                  isCategoryExpanded("state")
                    ? "Show less"
                    : "Show all (" +
                      application.service_region.state.length +
                      ")"
                }}</span>
                <svg
                  class="chevron-icon"
                  [class.expanded]="isCategoryExpanded('state')"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  width="16"
                  height="16"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            }
          </div>
          <div class="tags">
            @for (
              state of getVisibleItems(
                application.service_region.state,
                "state"
              );
              track state
            ) {
              <span class="tag">{{ state }}</span>
            }
            @if (application.service_region.state.length > REGION_ITEM_LIMIT) {
              @if (!isCategoryExpanded("state")) {
                <button
                  type="button"
                  class="tag tag-more"
                  (click)="toggleCategoryExpand('state')"
                  title="Show all states"
                >
                  +{{
                    application.service_region.state.length - REGION_ITEM_LIMIT
                  }}
                  more...
                </button>
              } @else {
                <button
                  type="button"
                  class="tag tag-less"
                  (click)="toggleCategoryExpand('state')"
                  title="Show less states"
                >
                  <span>Show less</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 640 640"
                    width="12"
                    height="12"
                    fill="currentColor"
                  >
                    <path
                      d="M297.4 201.4C309.9 188.9 330.2 188.9 342.7 201.4L502.7 361.4C515.2 373.9 515.2 394.2 502.7 406.7C490.2 419.2 469.9 419.2 457.4 406.7L320 269.3L182.6 406.6C170.1 419.1 149.8 419.1 137.3 406.6C124.8 394.1 124.8 373.8 137.3 361.3L297.3 201.3z"
                    />
                  </svg>
                </button>
              }
            }
          </div>
        </div>
      }
      @if (
        application.service_region.county &&
        application.service_region.county.length > 0
      ) {
        <div class="region-item">
          <div class="region-item-header">
            <span class="label">
              Counties
              <span class="item-count"
                >({{ application.service_region.county.length }})</span
              >
            </span>
            @if (application.service_region.county.length > REGION_ITEM_LIMIT) {
              <button
                type="button"
                class="btn-toggle-expand"
                (click)="toggleCategoryExpand('county')"
                [attr.aria-expanded]="isCategoryExpanded('county')"
              >
                <span>{{
                  isCategoryExpanded("county")
                    ? "Show less"
                    : "Show all (" +
                      application.service_region.county.length +
                      ")"
                }}</span>
                <svg
                  class="chevron-icon"
                  [class.expanded]="isCategoryExpanded('county')"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  width="16"
                  height="16"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            }
          </div>
          <div class="tags">
            @for (
              county of getVisibleItems(
                application.service_region.county,
                "county"
              );
              track county
            ) {
              <span class="tag">{{ county }}</span>
            }
            @if (application.service_region.county.length > REGION_ITEM_LIMIT) {
              @if (!isCategoryExpanded("county")) {
                <button
                  type="button"
                  class="tag tag-more"
                  (click)="toggleCategoryExpand('county')"
                  title="Show all counties"
                >
                  +{{
                    application.service_region.county.length - REGION_ITEM_LIMIT
                  }}
                  more...
                </button>
              } @else {
                <button
                  type="button"
                  class="tag tag-less"
                  (click)="toggleCategoryExpand('county')"
                  title="Show less counties"
                >
                  <span>Show less</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 640 640"
                    width="12"
                    height="12"
                    fill="currentColor"
                  >
                    <path
                      d="M297.4 201.4C309.9 188.9 330.2 188.9 342.7 201.4L502.7 361.4C515.2 373.9 515.2 394.2 502.7 406.7C490.2 419.2 469.9 419.2 457.4 406.7L320 269.3L182.6 406.6C170.1 419.1 149.8 419.1 137.3 406.6C124.8 394.1 124.8 373.8 137.3 361.3L297.3 201.3z"
                    />
                  </svg>
                </button>
              }
            }
          </div>
        </div>
      }
      @if (
        application.service_region.city &&
        application.service_region.city.length > 0
      ) {
        <div class="region-item">
          <div class="region-item-header">
            <span class="label">
              Cities
              <span class="item-count"
                >({{ application.service_region.city.length }})</span
              >
            </span>
            @if (application.service_region.city.length > REGION_ITEM_LIMIT) {
              <button
                type="button"
                class="btn-toggle-expand"
                (click)="toggleCategoryExpand('city')"
                [attr.aria-expanded]="isCategoryExpanded('city')"
              >
                <span>{{
                  isCategoryExpanded("city")
                    ? "Show less"
                    : "Show all (" +
                      application.service_region.city.length +
                      ")"
                }}</span>
                <svg
                  class="chevron-icon"
                  [class.expanded]="isCategoryExpanded('city')"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  width="16"
                  height="16"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            }
          </div>
          <div class="tags">
            @for (
              city of getVisibleItems(application.service_region.city, "city");
              track city
            ) {
              <span class="tag">{{ city }}</span>
            }
            @if (application.service_region.city.length > REGION_ITEM_LIMIT) {
              @if (!isCategoryExpanded("city")) {
                <button
                  type="button"
                  class="tag tag-more"
                  (click)="toggleCategoryExpand('city')"
                  title="Show all cities"
                >
                  +{{
                    application.service_region.city.length - REGION_ITEM_LIMIT
                  }}
                  more...
                </button>
              } @else {
                <button
                  type="button"
                  class="tag tag-less"
                  (click)="toggleCategoryExpand('city')"
                  title="Show less cities"
                >
                  <span>Show less</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 640 640"
                    width="12"
                    height="12"
                    fill="currentColor"
                  >
                    <path
                      d="M297.4 201.4C309.9 188.9 330.2 188.9 342.7 201.4L502.7 361.4C515.2 373.9 515.2 394.2 502.7 406.7C490.2 419.2 469.9 419.2 457.4 406.7L320 269.3L182.6 406.6C170.1 419.1 149.8 419.1 137.3 406.6C124.8 394.1 124.8 373.8 137.3 361.3L297.3 201.3z"
                    />
                  </svg>
                </button>
              }
            }
          </div>
        </div>
      }
      @if (
        application.service_region.zip_code &&
        application.service_region.zip_code.length > 0
      ) {
        <div class="region-item">
          <div class="region-item-header">
            <span class="label">
              Zip Codes
              <span class="item-count"
                >({{ application.service_region.zip_code.length }})</span
              >
            </span>
            @if (
              application.service_region.zip_code.length > REGION_ITEM_LIMIT
            ) {
              <button
                type="button"
                class="btn-toggle-expand"
                (click)="toggleCategoryExpand('zip_code')"
                [attr.aria-expanded]="isCategoryExpanded('zip_code')"
              >
                <span>{{
                  isCategoryExpanded("zip_code")
                    ? "Show less"
                    : "Show all (" +
                      application.service_region.zip_code.length +
                      ")"
                }}</span>
                <svg
                  class="chevron-icon"
                  [class.expanded]="isCategoryExpanded('zip_code')"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  width="16"
                  height="16"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            }
          </div>
          <div class="tags">
            @for (
              zipCode of getVisibleItems(
                application.service_region.zip_code,
                "zip_code"
              );
              track zipCode
            ) {
              <span class="tag">{{ zipCode }}</span>
            }
            @if (
              application.service_region.zip_code.length > REGION_ITEM_LIMIT
            ) {
              @if (!isCategoryExpanded("zip_code")) {
                <button
                  type="button"
                  class="tag tag-more"
                  (click)="toggleCategoryExpand('zip_code')"
                  title="Show all zip codes"
                >
                  +{{
                    application.service_region.zip_code.length -
                      REGION_ITEM_LIMIT
                  }}
                  more...
                </button>
              } @else {
                <button
                  type="button"
                  class="tag tag-less"
                  (click)="toggleCategoryExpand('zip_code')"
                  title="Show less zip codes"
                >
                  <span>Show less</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 640 640"
                    width="12"
                    height="12"
                    fill="currentColor"
                  >
                    <path
                      d="M297.4 201.4C309.9 188.9 330.2 188.9 342.7 201.4L502.7 361.4C515.2 373.9 515.2 394.2 502.7 406.7C490.2 419.2 469.9 419.2 457.4 406.7L320 269.3L182.6 406.6C170.1 419.1 149.8 419.1 137.3 406.6C124.8 394.1 124.8 373.8 137.3 361.3L297.3 201.3z"
                    />
                  </svg>
                </button>
              }
            }
          </div>
        </div>
      }
    </div>
  </div>

  <!-- Application Notes -->
  @if (application.application_notes) {
    <div class="section">
      <h3 class="section-title">Application Notes</h3>
      <div class="notes-box">
        {{ application.application_notes }}
      </div>
    </div>
  }

  <!-- Existing Approver Notes (if any) -->
  @if (
    application.approver_notes &&
    application.status !== ConnectionSelfSignupStatus.Pending
  ) {
    <div class="section">
      <h3 class="section-title">Approver Notes</h3>
      <div class="notes-box readonly">
        {{ application.approver_notes }}
      </div>
    </div>
  }

  <!-- Timestamps -->
  <!-- <div class="section">
      <div class="timestamps">
        <div class="timestamp-item">
          <span class="label">Submitted:</span>
          <span class="value">{{ application.created_on_utc | date: "medium" }}</span>
        </div>
        <div class="timestamp-item">
          <span class="label">Last Updated:</span>
          <span class="value">{{ application.updated_on_utc | date: "medium" }}</span>
        </div>
      </div>
    </div> -->
} @else if (isLoading) {
  <div class="loading">Loading application details...</div>
} @else if (errorMessage) {
  <div class="error-state">{{ errorMessage }}</div>
}

<!-- Approval Popup -->
@if (showApprovalPopup) {
  <app-popup
    heading="Approve Application"
    confirmText="Approve"
    cancelText="Cancel"
    [isConfirmDisabled]="approvalForm.invalid || isProcessing"
    (confirmClicked)="confirmApproval()"
    (cancelClicked)="cancelApproval()"
    [showCloseIcon]="true"
    [popupPosition]="'absolute'"
  >
    <div class="approval-popup-content">
      <form [formGroup]="approvalForm" class="approval-form">
        <!-- Connection Name Input -->
        <div class="form-group">
          <label for="connectionName" class="form-label">
            Connection Name <span class="required">*</span>
          </label>
          <input
            id="connectionName"
            type="text"
            formControlName="connectionName"
            class="form-input"
            [class.error]="
              approvalForm.get('connectionName')?.invalid &&
              approvalForm.get('connectionName')?.touched
            "
            placeholder="Enter connection name (alphanumeric and hyphens only)"
          />
          @if (
            approvalForm.get("connectionName")?.invalid &&
            approvalForm.get("connectionName")?.touched
          ) {
            <div class="error-message">{{ getConnectionNameError() }}</div>
          }
          <div class="form-help">
            Must start and end with alphanumeric characters. Hyphens allowed in
            between.
          </div>
        </div>

        <!-- Subscription Plan Selection -->
        <div class="form-group">
          <label for="subscriptionPlan" class="form-label">
            Subscription Plan <span class="optional">(Optional)</span>
          </label>
          @if (isLoadingPlans) {
            <div class="form-help">Loading subscription plans...</div>
          } @else {
            <select
              id="subscriptionPlan"
              class="form-input"
              [(ngModel)]="selectedPlanName"
              [ngModelOptions]="{ standalone: true }"
            >
              <option [ngValue]="null">No Subscription (Free Plan)</option>
              @for (plan of availablePlans; track plan.plan_name) {
                <option [ngValue]="plan.plan_name">
                  {{ plan.display_name }}
                </option>
              }
            </select>
          }
          <div class="form-help">
            Optionally assign a subscription plan. A trial will start when the
            first user logs in.
          </div>
        </div>

        <!-- Has IT Support -->
        <div class="form-group">
          <div class="form-label">
            Has IT Support <span class="required">*</span>
          </div>
          <div class="radio-group">
            <label class="radio-option">
              <input
                type="radio"
                formControlName="hasItSupport"
                [value]="true"
              />
              <span>Yes</span>
            </label>
            <label class="radio-option">
              <input
                type="radio"
                formControlName="hasItSupport"
                [value]="false"
              />
              <span>No</span>
            </label>
          </div>
          <div class="form-help">
            Indicates if this organization has an IT support team capable of
            configuring Auth0 connection.
            <!-- If "No", our support system will
              handle the configuration. -->
          </div>
        </div>
        <!-- Approver Notes -->
        <div class="form-group">
          <label for="approverNotes" class="form-label">
            Approver Notes <span class="optional">(Optional)</span>
          </label>
          <textarea
            id="approverNotes"
            [(ngModel)]="approverNotes"
            [ngModelOptions]="{ standalone: true }"
            class="form-textarea"
            placeholder="Optional notes about the approval..."
            rows="3"
          ></textarea>
        </div>
      </form>

      @if (errorMessage) {
        <div class="alert alert-error">
          {{ errorMessage }}
        </div>
      }
    </div>
  </app-popup>
}

<!-- Rejection Popup -->
@if (showRejectionPopup) {
  <app-popup
    heading="Reject Application"
    confirmText="Reject"
    cancelText="Cancel"
    [isConfirmDisabled]="!rejectionReason.trim() || isProcessing"
    (confirmClicked)="confirmRejection()"
    (cancelClicked)="cancelRejection()"
    [showCloseIcon]="true"
    [popupPosition]="'absolute'"
  >
    <div class="rejection-popup-content">
      <div class="rejection-warning">
        <div class="warning-icon">⚠️</div>
        <div class="warning-text">
          <h4>Are you sure you want to reject this application?</h4>
          <p>
            This action cannot be undone. Please provide reason for rejection.
          </p>
        </div>
      </div>

      <div class="form-group">
        <label for="rejectionReason" class="form-label">
          Rejection Reason <span class="required">*</span>
        </label>
        <textarea
          id="rejectionReason"
          [(ngModel)]="rejectionReason"
          class="form-textarea rejection-textarea"
          placeholder="Please provide a detailed reason for rejecting this application..."
          rows="4"
          [class.error]="!rejectionReason.trim() && rejectionTouched"
          (blur)="rejectionTouched = true"
        ></textarea>
        @if (!rejectionReason.trim() && rejectionTouched) {
          <div class="error-message">Rejection reason is required</div>
        }
      </div>

      @if (errorMessage) {
        <div class="alert alert-error">
          {{ errorMessage }}
        </div>
      }
    </div>
  </app-popup>
}
